import {
  deletePurchaseRequest,
  getPurchaseRequestDetail,
  getPurchaseRequestTypeTree,
  saveAndApplyPurchaseRequest,
} from "@/apis/requirementManagement.api";
import type {
  ApiResponse,
  SrmPurchaseRequestDto,
  SrmPurchaseRequestTypeTreeVo,
} from "@/types/apis";
import { getLabelByValue } from "@/utils/purchaseRequest";
import { useMutation, useQuery } from "@tanstack/react-query";

// Query Keys
export const purchaseRequestKeys = {
  detail: (id: number) => ["purchaseRequestDetail", id] as const,
  typeTree: ["purchaseRequest", "typeTree"] as const,
};

/**
 * 采购申请-根据ID获取采购申请详情
 */
export function usePurchaseRequestDetail(id: number | null) {
  const { data } = useQuery({
    queryKey: purchaseRequestKeys.detail(id ?? 0),
    queryFn: () => {
      if (id === null) {
        throw new Error("Purchase request ID is required");
      }
      return getPurchaseRequestDetail(id);
    },
    enabled: id !== null,
  });

  return data?.data.data;
}

/**
 * 删除采购申请
 */
export function useDeletePurchaseRequest(options?: {
  onSuccess?: (data: ApiResponse) => void;
}) {
  return useMutation({
    mutationFn: (ids: number[]) =>
      deletePurchaseRequest(ids).then((res) => res.data),
    onSuccess: options?.onSuccess,
  });
}

/**
 * 将采购申请类型树数据转换为Cascader组件所需格式
 */
export type CascaderOption = {
  label: string;
  value: string;
  children?: CascaderOption[];
};

function convertPurchaseRequestTypeTreeToCascaderOptions(
  data: SrmPurchaseRequestTypeTreeVo[],
): CascaderOption[] {
  return data.map((item) => ({
    label: item.desc,
    value: item.value,
    raw: item,
    children: item.children
      ? convertPurchaseRequestTypeTreeToCascaderOptions(item.children)
      : undefined,
  }));
}

/**
 * 获取采购申请类型结构树
 */
export function usePurchaseRequestTypeTree() {
  const { data } = useQuery({
    queryKey: purchaseRequestKeys.typeTree,
    queryFn: () => getPurchaseRequestTypeTree(),
  });

  const treeData = data?.data.data;
  const cascaderOptions = convertPurchaseRequestTypeTreeToCascaderOptions(
    treeData || [],
  );

  return {
    treeData,
    cascaderOptions,
    getLabelByValue: (value: string) =>
      treeData ? getLabelByValue(treeData, value) : undefined,
  };
}

/**
 * 保存并申请采购申请
 */
export function useSaveAndApplyPurchaseRequest(options?: {
  onSuccess?: (data: ApiResponse) => void;
}) {
  return useMutation({
    mutationFn: (data: SrmPurchaseRequestDto) =>
      saveAndApplyPurchaseRequest(data).then((res) => res.data),
    onSuccess: options?.onSuccess,
  });
}
